import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import { useInventoryOverview } from '@/hooks/useApi'
import type { CveSeverity, ProductOverview } from '@/api/client'
import { CveSeverityBadge } from '@/components/CveSeverityBadge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { AlertTriangle, ChevronRight, ExternalLink, Package, ShieldCheck } from 'lucide-react'

const SEVERITIES: CveSeverity[] = ['critical', 'high', 'medium', 'low', 'unknown']

// Ingeklapt tonen we de ergste componenten; de rest zit achter "toon alle".
const COLLAPSED_COMPONENTS = 5

function SeverityCounts({ counts }: { counts: Record<CveSeverity, number> }) {
  const present = SEVERITIES.filter((s) => counts[s] > 0)
  if (!present.length) return <span className="text-sm text-muted-foreground">geen open CVE's</span>
  return (
    <div className="flex flex-wrap gap-1.5">
      {present.map((s) => (
        <span key={s} className="inline-flex items-center gap-1">
          <CveSeverityBadge severity={s} />
          <span className="text-sm font-semibold tabular-nums">{counts[s]}</span>
        </span>
      ))}
    </div>
  )
}

/**
 * Een lege of oude inventaris is geen goed nieuws maar geen nieuws: "0 CVE's"
 * zegt dan niets. Dat moet op de kaart even luid zijn als een kritieke CVE.
 */
function FreshnessLine({ product }: { product: ProductOverview }) {
  if (!product.last_inventory_at) {
    return (
      <p className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        Nog geen SBOM ontvangen — over dit product zegt de inventaris nog niets
      </p>
    )
  }
  const age = formatDistanceToNow(new Date(product.last_inventory_at), { locale: nl, addSuffix: true })
  return product.stale ? (
    <p className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      Laatste SBOM {age} — verouderd, de CI/CD-push draait niet
    </p>
  ) : (
    <p className="text-sm text-muted-foreground">Laatste SBOM {age}</p>
  )
}

function ProductCard({ product }: { product: ProductOverview }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? product.components : product.components.slice(0, COLLAPSED_COMPONENTS)
  const hidden = product.components.length - shown.length
  const clean = product.component_count > 0 && product.vulnerable_component_count === 0

  return (
    <Card className={cn(product.kev_count > 0 && 'border-red-700/60', product.stale && 'border-amber-500/50')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Link to={`/inventory/${product.dossier_id}`} className="hover:underline">
                {product.name}
              </Link>
              {product.worst_severity && <CveSeverityBadge severity={product.worst_severity} />}
              {product.kev_count > 0 && (
                <Badge className="bg-red-700 text-white" title="Actief misbruikt (CISA KEV)">
                  ⚡ {product.kev_count} KEV
                </Badge>
              )}
              {clean && (
                <Badge variant="outline" className="text-green-700 dark:text-green-300 border-green-300 dark:border-green-800">
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" /> geen open CVE's
                </Badge>
              )}
            </CardTitle>
            {product.url && (
              <CardDescription>
                <a href={product.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                  {product.url.replace(/^https?:\/\//, '')} <ExternalLink className="h-3 w-3" />
                </a>
              </CardDescription>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/inventory/${product.dossier_id}`)}>
            Alle componenten <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        <FreshnessLine product={product} />
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Componenten" value={product.component_count}
                sub={product.os_component_count ? `waarvan ${product.os_component_count} OS-pakketten` : 'geen OS-pakketten in de SBOM'} />
          <Stat label="Kwetsbaar" value={product.vulnerable_component_count} warn={product.vulnerable_component_count > 0} />
          <Stat label="Verouderd" value={product.outdated_count} />
          <div>
            <div className="text-xs text-muted-foreground mb-1">Open CVE's</div>
            <SeverityCounts counts={product.severity_counts} />
          </div>
        </div>

        {product.components.length > 0 && (
          <div className="border rounded-md divide-y">
            {shown.map((c) => (
              <div key={c.component_id} className="px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CveSeverityBadge severity={c.worst_severity} />
                    {c.kev && <Badge className="bg-red-700 text-white">⚡ KEV</Badge>}
                    <span className="font-medium text-sm">{c.name}</span>
                    <span className="text-xs text-muted-foreground">{c.version}</span>
                    <span className="text-xs text-muted-foreground">· {c.ecosystem}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate" title={c.vuln_ids.join(', ')}>
                    {c.vuln_ids.slice(0, 3).join(', ')}
                    {c.vuln_ids.length > 3 && ` +${c.vuln_ids.length - 3}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm tabular-nums">{c.vuln_count} CVE{c.vuln_count === 1 ? '' : "'s"}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.fixed_versions.length ? `fix: ${c.fixed_versions.join(' / ')}` : 'geen fix bekend'}
                  </div>
                </div>
              </div>
            ))}
            {(hidden > 0 || expanded) && product.components.length > COLLAPSED_COMPONENTS && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 text-left"
              >
                {expanded ? 'Toon minder' : `Toon alle ${product.components.length} kwetsbare componenten`}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Stat({ label, value, sub, warn }: { label: string; value: number; sub?: string; warn?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-2xl font-bold tabular-nums', warn && 'text-red-600 dark:text-red-400')}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

export default function InventoryOverview() {
  const { data: products, isLoading, isError } = useInventoryOverview()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Package className="h-6 w-6" /> Inventaris per product
        </h1>
        <p className="text-muted-foreground">
          Componenten en hun kwetsbaarheden per product, het meest blootgestelde product bovenaan.
          Per CVE kijken kan op de Alerts-pagina.
        </p>
      </div>

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <p className="text-sm text-red-600">Inventaris kon niet geladen worden.</p>
      ) : !products?.length ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nog geen product levert een SBOM aan. Maak een SBOM-token aan op een dossier en laat de
            deploy-pipeline van dat product ernaar pushen.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {products.map((p) => (
            <ProductCard key={p.dossier_id} product={p} />
          ))}
        </div>
      )}
    </div>
  )
}
